import React, { useState, useEffect } from 'react';
import { Plus, Eye, FileText, Download, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [portCalls, setPortCalls] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [formData, setFormData] = useState({
    template_id: '',
    port_call_id: '',
    vessel_id: '',
    custom_data: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsRes, templatesRes, portCallsRes, vesselsRes] = await Promise.all([
        api.get('/documents'),
        api.get('/templates'),
        api.get('/port-calls'),
        api.get('/vessels')
      ]);
      setDocuments(docsRes.data);
      setTemplates(templatesRes.data);
      setPortCalls(portCallsRes.data);
      setVessels(vesselsRes.data);
    } catch (error) {
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/documents/generate', formData);
      toast.success('Belge başarıyla oluşturuldu');
      setPreviewContent(response.data.rendered_content);
      setSelectedDocument(response.data);
      setShowPreview(true);
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Belge oluşturulamadı');
    }
  };

  const resetForm = () => {
    setFormData({
      template_id: '',
      port_call_id: '',
      vessel_id: '',
      custom_data: {}
    });
  };

  const handleViewDocument = async (doc) => {
    setPreviewContent(doc.rendered_content);
    setSelectedDocument(doc);
    setShowPreview(true);
  };

  const handleDelete = async (documentId, documentName) => {
    if (!window.confirm(`"${documentName}" belgesini silmek istediğinizden emin misiniz?`)) return;
    
    try {
      await api.delete(`/documents/${documentId}`);
      toast.success('Belge başarıyla silindi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Belge silinemedi');
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      port_authority_petition: 'Port Authority Petition',
      crew_change_request: 'Crew Change Request',
      waste_collection_request: 'Waste Collection Request',
      agent_appointment_letter: 'Agent Appointment Letter',
      ship_arrival_notice: 'Ship Arrival Notice',
      ship_departure_notice: 'Ship Departure Notice',
      general_request: 'General Request'
    };
    return labels[type] || type;
  };

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title" data-testid="page-title">Belgeler ve Dilekçeler</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)} data-testid="generate-document-btn">
          <Plus size={18} />
          Belge Oluştur
        </button>
      </div>
      
      <div style={{ padding: '2rem' }}>
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">Available Templates</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
              {templates.map(template => (
                <div key={template.id} style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => { setFormData({ ...formData, template_id: template.id }); setShowModal(true); }} data-testid={`template-${template.id}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <FileText size={24} style={{ color: 'var(--primary)' }} />
                    <strong>{template.name}</strong>
                  </div>
                  {template.description && <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>{template.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Generated Documents</h3>
          </div>
          <div className="card-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
            ) : documents.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><FileText /></div>
                <h3 className="empty-state-title">Henüz belge oluşturulmadı</h3>
                <p className="empty-state-text">İlk belgenizi oluşturmak için yukarıdan bir şablon seçin</p>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Template</th>
                      <th>Generated By</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.id} data-testid={`document-row-${doc.id}`}>
                        <td>
                          <span className="badge badge-sent">{getDocumentTypeLabel(doc.document_type)}</span>
                        </td>
                        <td><strong>{doc.template_name}</strong></td>
                        <td>{doc.generated_by_name}</td>
                        <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button className="action-btn" onClick={() => handleViewDocument(doc)} title="View Document" data-testid={`view-document-${doc.id}`}>
                              <Eye size={18} />
                            </button>
                            <button 
                              className="action-btn action-btn-danger" 
                              onClick={() => handleDelete(doc.id, doc.template_name)}
                              title="Delete"
                              data-testid={`delete-document-${doc.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="modal-title">Belge Oluştur</h2>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleGenerate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Belge Şablonu *</label>
                  <select className="form-select" value={formData.template_id} onChange={(e) => setFormData({ ...formData, template_id: e.target.value })} required data-testid="template-select">
                    <option value="">Select Template</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Liman Çağrısı Bağlantısı (İsteğe Bağlı)</label>
                  <select className="form-select" value={formData.port_call_id} onChange={(e) => setFormData({ ...formData, port_call_id: e.target.value, vessel_id: '' })} data-testid="portcall-select">
                    <option value="">Select Port Call (optional)</option>
                    {portCalls.map(pc => (
                      <option key={pc.id} value={pc.id}>{pc.number} - {pc.vessel_name} ({pc.port})</option>
                    ))}
                  </select>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>Selecting a port call will auto-fill vessel and port information</div>
                </div>

                {!formData.port_call_id && (
                  <div className="form-group">
                    <label className="form-label">Veya Gemi Seçin (İsteğe Bağlı)</label>
                    <select className="form-select" value={formData.vessel_id} onChange={(e) => setFormData({ ...formData, vessel_id: e.target.value })} data-testid="vessel-select">
                      <option value="">Select Vessel (optional)</option>
                      {vessels.map(vessel => (
                        <option key={vessel.id} value={vessel.id}>{vessel.name} ({vessel.flag})</option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ background: '#f1f5f9', padding: '1rem', borderRadius: '8px', fontSize: '0.875rem', color: '#475569' }}>
                  <strong>📋 How it works:</strong>
                  <ol style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                    <li>Select a document template</li>
                    <li>Optionally link to a port call or vessel</li>
                    <li>System will auto-fill placeholders with data</li>
                    <li>Review and download the generated document</li>
                  </ol>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>İptal</button>
                <button type="submit" className="btn btn-primary" data-testid="generate-btn">Oluştur</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '90vh' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="preview-title">Belge Önizleme - {selectedDocument?.template_name}</h2>
              <button className="modal-close" onClick={() => setShowPreview(false)}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: 'calc(90vh - 200px)', overflow: 'auto' }}>
              <div style={{ background: 'white', padding: '2rem', minHeight: '600px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} dangerouslySetInnerHTML={{ __html: previewContent }} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPreview(false)}>Close</button>
              <button className="btn btn-primary" onClick={() => { const printWindow = window.open('', '_blank'); printWindow.document.write(previewContent); printWindow.document.close(); printWindow.print(); }} data-testid="print-btn">
                <Download size={18} />
                Print / Save as PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
